import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { BarChart3, TrendingUp, Users, Activity, Calendar, Video, MessageSquare, Target } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlatformAdminAnalytics = () => {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/platform-admin/dashboard-full`, { headers: getAuthHeaders() });
        setData(response.data);
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      }
      setLoading(false);
    };
    fetchData();
  }, [getAuthHeaders]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Calculate metrics
  const totalUsers = (data?.users?.total_coaches || 0) + (data?.users?.total_parents || 0);
  const coachLedPercent = data?.acquisition?.coach_led && data?.acquisition?.athlete_led 
    ? Math.round((data.acquisition.coach_led / (data.acquisition.coach_led + data.acquisition.athlete_led)) * 100)
    : 0;
  const athleteLedPercent = 100 - coachLedPercent;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl uppercase tracking-tight">KPIs & Analytics</h1>
        <p className="text-muted-foreground">Platform performance metrics and insights</p>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500/20 to-blue-500/5 border border-blue-500/30 p-6 rounded-sm">
          <Users className="text-blue-400 mb-2" size={24} />
          <p className="text-3xl font-heading">{totalUsers}</p>
          <p className="text-sm text-muted-foreground">Total Users</p>
        </div>
        <div className="bg-gradient-to-br from-green-500/20 to-green-500/5 border border-green-500/30 p-6 rounded-sm">
          <TrendingUp className="text-green-400 mb-2" size={24} />
          <p className="text-3xl font-heading">${data?.revenue?.total_mrr || 0}</p>
          <p className="text-sm text-muted-foreground">MRR</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500/20 to-purple-500/5 border border-purple-500/30 p-6 rounded-sm">
          <Activity className="text-purple-400 mb-2" size={24} />
          <p className="text-3xl font-heading">{data?.activity?.sessions_30d || 0}</p>
          <p className="text-sm text-muted-foreground">Sessions (30d)</p>
        </div>
        <div className="bg-gradient-to-br from-amber-500/20 to-amber-500/5 border border-amber-500/30 p-6 rounded-sm">
          <Target className="text-amber-400 mb-2" size={24} />
          <p className="text-3xl font-heading">{data?.revenue?.active_passes || 0}</p>
          <p className="text-sm text-muted-foreground">Active Passes</p>
        </div>
      </div>

      {/* User Acquisition */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-card border border-border/40 p-6 rounded-sm">
          <div className="flex items-center gap-2 mb-6">
            <BarChart3 className="text-primary" size={20} />
            <h2 className="font-heading text-xl uppercase">User Acquisition</h2>
          </div>
          
          <div className="space-y-6">
            {/* Coach-Led */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm">Coach-Led Acquisition</span>
                <span className="font-heading text-lg">{data?.acquisition?.coach_led || 0}</span>
              </div>
              <div className="w-full h-4 bg-muted/50 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-400 transition-all duration-500"
                  style={{ width: `${coachLedPercent}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">{coachLedPercent}% of total</p>
            </div>

            {/* Athlete-Led */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm">Athlete-Led Acquisition</span>
                <span className="font-heading text-lg">{data?.acquisition?.athlete_led || 0}</span>
              </div>
              <div className="w-full h-4 bg-muted/50 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-400 transition-all duration-500"
                  style={{ width: `${athleteLedPercent}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">{athleteLedPercent}% of total</p>
            </div>
          </div>
        </div>

        {/* User Breakdown */}
        <div className="bg-card border border-border/40 p-6 rounded-sm">
          <div className="flex items-center gap-2 mb-6">
            <Users className="text-primary" size={20} />
            <h2 className="font-heading text-xl uppercase">User Breakdown</h2>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-sm text-center">
              <p className="text-2xl font-heading text-blue-400">{data?.users?.owner_coaches || 0}</p>
              <p className="text-xs text-muted-foreground uppercase">Subscribed Coaches</p>
            </div>
            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-sm text-center">
              <p className="text-2xl font-heading text-amber-400">{data?.users?.invited_coaches || 0}</p>
              <p className="text-xs text-muted-foreground uppercase">Invited Coaches</p>
            </div>
            <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-sm text-center">
              <p className="text-2xl font-heading text-purple-400">{data?.users?.total_athletes || 0}</p>
              <p className="text-xs text-muted-foreground uppercase">Athletes</p>
            </div>
            <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-sm text-center">
              <p className="text-2xl font-heading text-cyan-400">{data?.users?.total_parents || 0}</p>
              <p className="text-xs text-muted-foreground uppercase">Parents</p>
            </div>
          </div>
        </div>
      </div>

      {/* Engagement Metrics */}
      <div className="bg-card border border-border/40 p-6 rounded-sm">
        <div className="flex items-center gap-2 mb-6">
          <Activity className="text-primary" size={20} />
          <h2 className="font-heading text-xl uppercase">Engagement Metrics</h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-muted/30 rounded-sm text-center">
            <Calendar className="mx-auto text-primary mb-2" size={24} />
            <p className="text-2xl font-heading">{data?.activity?.sessions_7d || 0}</p>
            <p className="text-xs text-muted-foreground">Sessions (7d)</p>
          </div>
          <div className="p-4 bg-muted/30 rounded-sm text-center">
            <Calendar className="mx-auto text-blue-400 mb-2" size={24} />
            <p className="text-2xl font-heading">{data?.activity?.sessions_30d || 0}</p>
            <p className="text-xs text-muted-foreground">Sessions (30d)</p>
          </div>
          <div className="p-4 bg-muted/30 rounded-sm text-center">
            <Video className="mx-auto text-purple-400 mb-2" size={24} />
            <p className="text-2xl font-heading">-</p>
            <p className="text-xs text-muted-foreground">Videos (30d)</p>
          </div>
          <div className="p-4 bg-muted/30 rounded-sm text-center">
            <MessageSquare className="mx-auto text-green-400 mb-2" size={24} />
            <p className="text-2xl font-heading">-</p>
            <p className="text-xs text-muted-foreground">Feedback (30d)</p>
          </div>
        </div>
      </div>

      {/* Revenue Breakdown */}
      <div className="bg-card border border-border/40 p-6 rounded-sm">
        <div className="flex items-center gap-2 mb-6">
          <TrendingUp className="text-primary" size={20} />
          <h2 className="font-heading text-xl uppercase">Revenue Breakdown</h2>
        </div>

        <div className="grid md:grid-cols-4 gap-4">
          <div className="p-4 bg-muted/30 rounded-sm">
            <p className="text-sm text-muted-foreground">Coach Subscriptions</p>
            <p className="text-xl font-heading mt-1">${data?.revenue?.coach_mrr || 0}</p>
            <div className="w-full h-1 bg-muted/50 rounded-full mt-2 overflow-hidden">
              <div 
                className="h-full bg-blue-400"
                style={{ width: `${data?.revenue?.total_mrr ? (data.revenue.coach_mrr / data.revenue.total_mrr) * 100 : 0}%` }}
              />
            </div>
          </div>
          <div className="p-4 bg-muted/30 rounded-sm">
            <p className="text-sm text-muted-foreground">Athlete Pass (Base)</p>
            <p className="text-xl font-heading mt-1">${data?.revenue?.pass_mrr || 0}</p>
            <div className="w-full h-1 bg-muted/50 rounded-full mt-2 overflow-hidden">
              <div 
                className="h-full bg-green-400"
                style={{ width: `${data?.revenue?.total_mrr ? (data.revenue.pass_mrr / data.revenue.total_mrr) * 100 : 0}%` }}
              />
            </div>
          </div>
          <div className="p-4 bg-primary/10 border border-primary/30 rounded-sm">
            <p className="text-sm text-muted-foreground">Athlete Pass Plus</p>
            <p className="text-xl font-heading text-primary mt-1">${data?.revenue?.plus_mrr || 0}</p>
            <div className="w-full h-1 bg-muted/50 rounded-full mt-2 overflow-hidden">
              <div 
                className="h-full bg-primary"
                style={{ width: `${data?.revenue?.total_mrr ? (data.revenue.plus_mrr / data.revenue.total_mrr) * 100 : 0}%` }}
              />
            </div>
          </div>
          <div className="p-4 bg-gradient-to-br from-green-500/20 to-green-500/5 border border-green-500/30 rounded-sm">
            <p className="text-sm text-muted-foreground">Total MRR</p>
            <p className="text-xl font-heading text-green-400 mt-1">${data?.revenue?.total_mrr || 0}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformAdminAnalytics;
